import { useState, useRef, useEffect } from 'react'
import {
  Send,
  Plane,
  FileText,
  Plus,
  ChevronRight,
  X,
  Copy,
  Check,
  ExternalLink,
  Sparkles,
  Trash2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

interface Citation {
  id: string
  reference: string
  manual: string
  section: string
  pages: string
  revision: string
  content: string
}

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  citations?: Citation[]
  timestamp: Date
}

interface Conversation {
  id: string
  title: string
  aircraft?: string
  messages: Message[]
  lastMessage: string
  timestamp: Date
}

// Initial sample conversation
const createInitialConversation = (): Conversation => ({
  id: '1',
  title: 'HA-420 HF120 Oil Service',
  aircraft: 'HA-420',
  lastMessage: 'The GE Honda HF120 engine oil...',
  timestamp: new Date(Date.now() - 1000 * 60 * 5),
  messages: [
    {
      id: '1',
      role: 'user',
      content: 'What is the engine oil service procedure for the GE Honda HF120 on the HA-420?',
      timestamp: new Date(Date.now() - 1000 * 60 * 2),
    },
    {
      id: '2',
      role: 'assistant',
      content: `**GE Honda HF120 Engine Oil Service**
**Aircraft:** HondaJet HA-420
**ATA Chapter:** 79-20-00

**Oil Specification:**
Approved oil: **Mobil Jet Oil II** (MIL-PRF-23699 HTS)
Oil capacity: 4.5 quarts per engine
Minimum serviceable: 2.5 quarts

**Service Interval:**
Per ALIM, oil servicing is part of every **Phase 2 inspection** (150-300 hr interval). Check oil level at every preflight per AMM 12-20-00.

**Procedure:**
1. Ensure engine is cool (min 30 min after shutdown)
2. Open nacelle cowling per ATA 71 access procedures
3. Remove oil filler cap (upper-right, engine accessory section)
4. Check dipstick — level must be between MIN and MAX marks
5. Add oil in 0.5-qt increments if below MIN
6. Replace filler cap and secure

⚠️ **WARNING:** Do not overfill above MAX mark. Overfilling may cause oil foaming and bearing damage. [1]

**Oil Analysis:**
Oil sample should be taken at each Phase 2 for spectrometric analysis. Elevated iron (>5 ppm) or chromium (>2 ppm) requires investigation per CMM 72-00-00. [2]`,
      citations: [
        {
          id: 'c1',
          reference: '[1]',
          manual: 'HA-420 AMM',
          section: '79-20-00',
          pages: '401-403',
          revision: 'Rev J',
          content: 'GE HONDA HF120 — ENGINE OIL SERVICING\n\nWARNING: Do not overfill engine oil above MAX mark on dipstick. Overfilling may cause oil foaming, leading to insufficient lubrication of main bearings and potential engine damage.\n\nApproved Oil: Mobil Jet Oil II (MIL-PRF-23699 HTS)\nCapacity: 4.5 quarts per engine\nMin serviceable: 2.5 quarts',
        },
        {
          id: 'c2',
          reference: '[2]',
          manual: 'HF120 CMM',
          section: '72-00-00',
          pages: '302-304',
          revision: 'Rev 8',
          content: 'OIL ANALYSIS PROGRAM\n\nSpectrometric oil analysis (SOAP) required at each Phase 2 inspection interval.\n\nAction limits:\n- Iron (Fe): >5 ppm — investigate wear source\n- Chromium (Cr): >2 ppm — investigate bearing surfaces\n- Silicon (Si): >25 ppm — check air filtration path\n\nTrend monitoring is critical. Any single parameter exceeding 150% of baseline requires engine removal for investigation.',
        },
      ],
      timestamp: new Date(Date.now() - 1000 * 60 * 1),
    },
  ],
})

export function ChatPage() {
  const [conversations, setConversations] = useState<Conversation[]>([createInitialConversation()])
  const [activeConversationId, setActiveConversationId] = useState<string>('1')
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [selectedCitation, setSelectedCitation] = useState<Citation | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [selectedAircraft, setSelectedAircraft] = useState('HA-420')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const activeConversation = conversations.find(c => c.id === activeConversationId)
  const messages = activeConversation?.messages || []

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const handleNewConversation = () => {
    const newId = Date.now().toString()
    const newConversation: Conversation = {
      id: newId,
      title: 'New Conversation',
      aircraft: selectedAircraft,
      messages: [],
      lastMessage: 'Start a new conversation...',
      timestamp: new Date(),
    }
    setConversations(prev => [newConversation, ...prev])
    setActiveConversationId(newId)
    setSelectedCitation(null)
  }

  const handleSelectConversation = (convId: string) => {
    setActiveConversationId(convId)
    setSelectedCitation(null)
    const conv = conversations.find(c => c.id === convId)
    if (conv?.aircraft) {
      setSelectedAircraft(conv.aircraft)
    }
  }

  const handleDeleteConversation = (convId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setConversations(prev => prev.filter(c => c.id !== convId))
    if (activeConversationId === convId) {
      const remaining = conversations.filter(c => c.id !== convId)
      if (remaining.length > 0) {
        setActiveConversationId(remaining[0].id)
      } else {
        handleNewConversation()
      }
    }
  }

  const generateConversationTitle = (query: string): string => {
    // Extract key terms for title
    const words = query.split(' ').slice(0, 5).join(' ')
    return words.length > 30 ? words.substring(0, 30) + '...' : words
  }

  const handleSend = async () => {
    if (!input.trim() || isLoading) return

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date(),
    }

    const query = input
    setInput('')
    setIsLoading(true)

    // Update conversation with user message
    setConversations(prev => prev.map(conv => {
      if (conv.id === activeConversationId) {
        const isFirstMessage = conv.messages.length === 0
        return {
          ...conv,
          title: isFirstMessage ? generateConversationTitle(query) : conv.title,
          aircraft: selectedAircraft,
          messages: [...conv.messages, userMessage],
          lastMessage: query.substring(0, 50) + '...',
          timestamp: new Date(),
        }
      }
      return conv
    }))

    try {
      // Call the backend chat API
      const token = localStorage.getItem('voltaire_token')
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          query: query,
          aircraft_type: selectedAircraft,
          conversation_id: activeConversationId,
        }),
      })

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`)
      }

      const data = await response.json()

      // Transform backend citations to frontend format
      const citations: Citation[] = (data.citations || []).map((c: any, idx: number) => ({
        id: `c${idx + 1}`,
        reference: `[${idx + 1}]`,
        manual: c.manual || `${selectedAircraft} AMM`,
        section: c.section_number || c.section || '00-00-00',
        pages: c.pages || `${c.page_start || 0}-${c.page_end || 0}`,
        revision: c.revision || 'Rev 1',
        content: c.content || c.text || '',
      }))

      const assistantMessage: Message = {
        id: data.message_id || (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.response || data.answer || 'No response received.',
        citations: citations.length > 0 ? citations : undefined,
        timestamp: new Date(),
      }

      // Update conversation with assistant message
      setConversations(prev => prev.map(conv => {
        if (conv.id === activeConversationId) {
          return {
            ...conv,
            messages: [...conv.messages, assistantMessage],
            lastMessage: (data.response || '').substring(0, 50) + '...',
            timestamp: new Date(),
          }
        }
        return conv
      }))
    } catch (error) {
      // Fallback response if API is unavailable
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `I apologize, but I'm having trouble connecting to the maintenance documentation system. Please try again in a moment.\n\nError: ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: new Date(),
      }
      setConversations(prev => prev.map(conv => {
        if (conv.id === activeConversationId) {
          return {
            ...conv,
            messages: [...conv.messages, assistantMessage],
            timestamp: new Date(),
          }
        }
        return conv
      }))
    } finally {
      setIsLoading(false)
    }
  }

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const formatDate = (date: Date) => {
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))

    if (days === 0) return formatTime(date)
    if (days === 1) return 'Yesterday'
    if (days < 7) return `${days} days ago`
    return date.toLocaleDateString()
  }

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      {/* Conversation Sidebar */}
      <div className="hidden w-72 border-r bg-gray-50 lg:block">
        <div className="flex h-14 items-center justify-between border-b px-4">
          <h2 className="font-semibold text-gray-900">Conversations</h2>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={handleNewConversation}
            title="New Conversation"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <ScrollArea className="h-[calc(100%-3.5rem)]">
          <div className="p-2">
            {conversations.map((conv) => (
              <div
                key={conv.id}
                className={cn(
                  'group relative w-full rounded-lg p-3 text-left transition-colors hover:bg-gray-100 cursor-pointer',
                  conv.id === activeConversationId && 'bg-white shadow-sm'
                )}
                onClick={() => handleSelectConversation(conv.id)}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900 truncate pr-2">
                    {conv.title}
                  </span>
                  <div className="flex items-center gap-1">
                    {conv.aircraft && (
                      <Badge variant="secondary" className="text-xs">
                        {conv.aircraft}
                      </Badge>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => handleDeleteConversation(conv.id, e)}
                    >
                      <Trash2 className="h-3 w-3 text-gray-400 hover:text-red-500" />
                    </Button>
                  </div>
                </div>
                <p className="mt-1 text-sm text-gray-500 truncate">
                  {conv.lastMessage}
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  {formatDate(conv.timestamp)}
                </p>
              </div>
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Main Chat Area */}
      <div className="flex flex-1 flex-col">
        {/* Chat Header */}
        <div className="flex h-14 items-center justify-between border-b px-4">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-navy-900">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Voltaire Assistant</h2>
              <p className="text-xs text-gray-500">Aviation maintenance intelligence</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={selectedAircraft}
              onChange={(e) => setSelectedAircraft(e.target.value)}
              className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-voltaire-blue"
            >
              <optgroup label="HondaJet">
                <option value="HA-420">HA-420 HondaJet</option>
                <option value="HA-420-Elite">HA-420 Elite</option>
                <option value="HA-420-EliteS">HA-420 Elite S</option>
                <option value="HA-420-EliteII">HA-420 Elite II</option>
              </optgroup>
              <optgroup label="Boeing">
                <option value="B737-800">B737-800</option>
                <option value="B737-MAX">B737-MAX</option>
                <option value="B777-300ER">B777-300ER</option>
                <option value="B787-9">B787-9</option>
              </optgroup>
              <optgroup label="Airbus">
                <option value="A320">A320</option>
                <option value="A321">A321</option>
              </optgroup>
            </select>
          </div>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 p-4">
          <div className="mx-auto max-w-3xl space-y-6">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 mb-4">
                  <Plane className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900">Start a new conversation</h3>
                <p className="text-sm text-gray-500 mt-1 max-w-md">
                  Ask about maintenance procedures, specifications, troubleshooting, or any aviation maintenance question.
                </p>
              </div>
            )}

            {messages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  'message-enter',
                  message.role === 'user' ? 'flex justify-end' : ''
                )}
              >
                {message.role === 'user' ? (
                  <div className="max-w-[80%] rounded-2xl bg-navy-900 px-4 py-3 text-white">
                    <p className="whitespace-pre-wrap">{message.content}</p>
                    <p className="mt-2 text-xs text-gray-400">
                      {formatTime(message.timestamp)}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-voltaire-blue">
                        <Plane className="h-4 w-4 text-white" />
                      </div>
                      <div className="flex-1 space-y-3">
                        <div className="rounded-2xl bg-white border px-4 py-3 shadow-sm">
                          <div className="prose prose-sm max-w-none">
                            {message.content.split('\n').map((line, i) => {
                              if (line.startsWith('**') && line.endsWith('**')) {
                                return (
                                  <p key={i} className="font-semibold text-gray-900">
                                    {line.replace(/\*\*/g, '')}
                                  </p>
                                )
                              }
                              if (line.startsWith('⚠️')) {
                                return (
                                  <div key={i} className="my-2 rounded-lg bg-yellow-50 border border-yellow-200 p-3 text-yellow-800">
                                    {line}
                                  </div>
                                )
                              }
                              if (line.startsWith('•')) {
                                return (
                                  <p key={i} className="ml-4 text-gray-700">{line}</p>
                                )
                              }
                              if (line.match(/^\d+\./)) {
                                return (
                                  <p key={i} className="ml-4 text-gray-700">{line}</p>
                                )
                              }
                              if (line.trim()) {
                                return <p key={i}>{line}</p>
                              }
                              return <br key={i} />
                            })}
                          </div>

                          {/* Citations */}
                          {message.citations && message.citations.length > 0 && (
                            <div className="mt-4 space-y-2 border-t pt-4">
                              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Sources
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {message.citations.map((citation) => (
                                  <button
                                    key={citation.id}
                                    onClick={() => setSelectedCitation(citation)}
                                    className="inline-flex items-center gap-1.5 rounded-lg bg-blue-50 px-3 py-1.5 text-sm text-blue-700 hover:bg-blue-100 transition-colors"
                                  >
                                    <FileText className="h-3.5 w-3.5" />
                                    <span>{citation.manual} {citation.section}</span>
                                    <ChevronRight className="h-3.5 w-3.5" />
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-gray-400">
                          {formatTime(message.timestamp)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Loading indicator */}
            {isLoading && (
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-voltaire-blue">
                  <Plane className="h-4 w-4 text-white" />
                </div>
                <div className="rounded-2xl bg-white border px-4 py-3 shadow-sm">
                  <div className="typing-indicator text-gray-400">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Input Area */}
        <div className="border-t bg-white p-4">
          <div className="mx-auto max-w-3xl">
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <div className="relative">
                  <Input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        handleSend()
                      }
                    }}
                    placeholder="Ask about maintenance procedures, specifications, troubleshooting..."
                    className="h-12 pr-12 resize-none"
                    disabled={isLoading}
                  />
                  <Button
                    onClick={handleSend}
                    disabled={!input.trim() || isLoading}
                    size="icon"
                    className="absolute right-1.5 top-1.5 h-9 w-9"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
            <p className="mt-2 text-center text-xs text-gray-400">
              Voltaire AI provides information from indexed maintenance manuals. Always verify critical procedures.
            </p>
          </div>
        </div>
      </div>

      {/* Citation Panel */}
      {selectedCitation && (
        <div className="w-96 border-l bg-white">
          <div className="flex h-14 items-center justify-between border-b px-4">
            <h3 className="font-semibold text-gray-900">Source Document</h3>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setSelectedCitation(null)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <ScrollArea className="h-[calc(100%-3.5rem)]">
            <div className="p-4 space-y-4">
              {/* Citation metadata */}
              <div className="rounded-lg bg-gray-50 p-4 space-y-3">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Manual</p>
                  <p className="font-medium text-gray-900">{selectedCitation.manual}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Section</p>
                    <p className="font-medium text-gray-900">{selectedCitation.section}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Pages</p>
                    <p className="font-medium text-gray-900">{selectedCitation.pages}</p>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Revision</p>
                  <p className="font-medium text-gray-900">{selectedCitation.revision}</p>
                </div>
              </div>

              {/* Citation content */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Content</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => handleCopy(selectedCitation.content, selectedCitation.id)}
                  >
                    {copiedId === selectedCitation.id ? (
                      <>
                        <Check className="mr-1 h-3 w-3" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="mr-1 h-3 w-3" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
                <div className="rounded-lg border bg-white p-4">
                  <pre className="whitespace-pre-wrap font-mono text-sm text-gray-700">
                    {selectedCitation.content}
                  </pre>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" size="sm">
                  <ExternalLink className="mr-2 h-4 w-4" />
                  View Full Document
                </Button>
              </div>
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
  )
}
